import {useGetSignalValueTrackersQuery} from '@apis/base'
import {useGetSignalByIdQuery} from '@apis/lineSignals'
import BasicDataGrid from '@shared/components/base/BasicDataGrid/BasicDataGrid.tsx'
import BasicHierarchyFilterGroup from '@shared/components/base/filters/BasicHierarchyFilterGroup.tsx'
import SimpleSearchWithSearchParams from '@shared/components/SimpleSearchWithSearchParams'
import BasicDashboardContainer from '@shared/components/BasicDashboardContainer.tsx'
import {formatDateTime} from '@shared/utils/dateUtils.ts'
import {useCurrentUser} from '@hooks/useCurrentUser.ts'
import {useSearchParams} from 'react-router-dom'
import useDataGridState from '@hooks/useDataGridState.ts'
import {useCustomTranslation} from '@hooks/useCustomTranslation.ts'
import {useMeasuredHeightOffset} from '@shared/hooks/useMeasuredHeightOffset'
import type {GridColDef} from '@mui/x-data-grid'
import type {ReactElement} from 'react'
import {Box, Chip} from '@mui/material'
import {SignalValueTrackerStatusTypes} from '@shared/types/signalValueTrackerStatusTypes.ts'
import DateIntervalPicker from '@shared/components/timerange/DateIntervalPicker'
import {DEFAULT_TIME_RANGE_OPTIONS} from '@shared/constants/timeRangeOptions'
import {
  type TimeIntervalContextProps,
  useTimeIntervalContext,
} from '@shared/providers/TimeIntervalProvider'
import ExportSignalValueTrackersExcel from './ExportSignalValueTrackersExcel'

const SignalValueTrackersTableComponent = (): ReactElement => {
  const [t] = useCustomTranslation('common')
  const [tLineSignals] = useCustomTranslation('line_signals')
  const {currentUser} = useCurrentUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const {startTime, endTime} = useTimeIntervalContext() as TimeIntervalContextProps
  const {
    sortModel,
    handleSortModelChange,
    selectedRows,
    handleModelChange,
    paginationModel,
    handlePaginationModelChange,
  } = useDataGridState()

  const statusTranslationMap: Record<SignalValueTrackerStatusTypes, string> = {
    [SignalValueTrackerStatusTypes.RUNNING]: t('status.running'),
    [SignalValueTrackerStatusTypes.COMPLETED]: t('status.completed'),
    [SignalValueTrackerStatusTypes.ERROR]: t('status.error'),
  }

  const searchTerm = searchParams.get('searchSignalValueTracker')
  // Set by the dashboard's Signal Values widget link (AB#6499): the trackers of that one
  // signal, so a value another signal of the same equipment also takes is not listed.
  const signalId = searchParams.get('signalId') ?? undefined
  const {data: pinnedSignal} = useGetSignalByIdQuery({signalId: signalId ?? ''}, {skip: !signalId})
  const clearSignalFilter = (): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('signalId')
        return next
      },
      {replace: true},
    )
  }

  // AB#5657 follow-up: search + the 3-filter hierarchy row overflowed / squished the search
  // box at narrower widths (no wrap anywhere in the chain). Own search + wrap below the
  // measured breakpoint, height driven by the row's own live-measured height.
  const [filterRowRef, gridHeightOffset] = useMeasuredHeightOffset(157)

  const filters = {
    productionAreaId: searchParams.get('productionAreaId') ?? '',
    lineId: searchParams.get('lineId') ?? '',
    equipmentId: searchParams.get('equipmentId') ?? '',
  }

  const {data, isFetching, isError, error} = useGetSignalValueTrackersQuery({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    ordering:
      sortModel.length > 0
        ? `${sortModel[0].sort === 'desc' ? '-' : ''}${sortModel[0].field}`
        : undefined,
    search: searchTerm as string,
    startDate: startTime.toISOString(),
    endDate: endTime.toISOString(),
    signalId,
    filters: filters,
  })

  const rows =
    data?.results?.map((tracker) => ({
      ...tracker,
      signal_name: tracker.signal?.name ?? 'N/A',
      equipment_name: tracker.equipment?.name ?? 'N/A',
      t_start: formatDateTime(tracker.t_start, currentUser),
      t_end: tracker.t_end ? formatDateTime(tracker.t_end, currentUser) : '',
      status:
        statusTranslationMap[tracker.status as SignalValueTrackerStatusTypes] ?? tracker.status,
    })) ?? []

  const columns: GridColDef[] = [
    {
      field: 'equipment_name',
      headerName: t('equipment.title'),
      flex: 1,
      editable: false,
      filterable: false,
      sortable: false,
    },
    {
      field: 'signal_name',
      headerName: t('signal'),
      flex: 1,
      editable: false,
      filterable: false,
    },
    {
      field: 'status',
      headerName: t('status.title'),
      flex: 1,
      editable: false,
      filterable: false,
    },
    {
      field: 't_start',
      headerName: t('t_start'),
      flex: 1,
      editable: false,
      filterable: false,
    },
    {
      field: 't_end',
      headerName: t('t_end'),
      flex: 1,
      editable: false,
      filterable: false,
    },
    {
      field: 'value',
      headerName: t('value'),
      flex: 1,
      editable: false,
      filterable: false,
    },
    {
      field: 'signal_value_description',
      headerName: tLineSignals('signal_description'),
      flex: 1,
      valueGetter: (_, row): string => {
        return row.signal_value_description?.description ?? ''
      },
      filterable: false,
    },
  ]

  return (
    <BasicDashboardContainer data-testid="basic-dashboard-container-signal-value-trackers-table">
      <BasicDataGrid
        columns={columns}
        rows={rows}
        rowCount={data?.count || 0}
        title={''}
        loading={isFetching}
        rowHeight={50}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        height={gridHeightOffset}
        error={isError}
        queryError={error}
        noRowsMessage={t('no_data')}
        onSortModelChange={handleSortModelChange}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleModelChange}
        titleSearchParams="searchSignalValueTracker"
        hideSearch
        customLeftHeaderActions={
          <Box
            ref={filterRowRef}
            sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, rowGap: 1}}
          >
            <Box sx={{width: 200, flexShrink: 0}}>
              <SimpleSearchWithSearchParams title="searchSignalValueTracker" size="small" />
            </Box>
            <BasicHierarchyFilterGroup />
            {signalId ? (
              <Chip
                size="small"
                label={`${t('signal')}: ${pinnedSignal?.name ?? '…'}`}
                onDelete={clearSignalFilter}
                data-testid="mbai-svt-signal-filter-chip"
              />
            ) : null}
          </Box>
        }
        customRightHeaderActions={
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <DateIntervalPicker
              timeRangeOptions={DEFAULT_TIME_RANGE_OPTIONS}
              navIsLoading={isFetching}
              navHasError={isError}
              navHasData={!!(data && data.results && data.results.length > 0)}
            />
            <ExportSignalValueTrackersExcel disabled={isFetching} />
          </Box>
        }
      />
    </BasicDashboardContainer>
  )
}

export default SignalValueTrackersTableComponent
